import React, { useState } from 'react';

import type { Leaves } from '@aline/common';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import { Formik } from 'formik';
import type { CurrentUserType } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { OrgSize, OrgType } from 'src/gql/generated/apollo-types';
import { makeStyles } from 'tss-react/mui';
import * as yup from 'yup';

import type { OnContinueFunction } from './OnboardingContainer';
import { FormikAlineInput } from '../FormikAlineInput';
import { Spacer } from '../generic';
import { AlineButton } from '../generic/AlineButton';

export const usePreOnboardingStepStyles = makeStyles()(theme => ({
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(4),
    width: '100%',
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  fieldGroup: {
    marginBottom: theme.spacing(1),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    width: '100%',
  },
  formControl: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  formControlWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  formLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginLeft: 0,
    textAlign: 'left',
    width: '100%',
  },
  formWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  input: {
    height: 40,
    padding: theme.spacing(1, 1.5),
  },
  organizationTypes: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    marginTop: theme.spacing(0.5),
    width: '100%',
  },
  radioOption: {
    alignItems: 'center',
    border: '1px solid #E0E0E0',
    borderRadius: 16,
    display: 'flex',
    margin: 0,
    padding: theme.spacing(0.5),
  },
  selectInput: {
    padding: theme.spacing(1.5),
  },
}));

interface FormValues {
  firstName: string;
  lastName: string;
  organizationSize: OrgSize;
  organizationType: OrgType;
  website: string;
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  organizationSize: yup.string().required(),
  organizationType: yup.string().required(),
  website: yup.string().url('Please enter a valid URL').optional(),
});

const websiteKey: Leaves<FormValues> = 'website';
const lastNameKey: Leaves<FormValues> = 'lastName';
const firstNameKey: Leaves<FormValues> = 'firstName';
const organizationTypeKey: Leaves<FormValues> = 'organizationType';
const organizationSizeKey: Leaves<FormValues> = 'organizationSize';

interface DemographicInfoProps {
  currentUser: CurrentUserType;
  onContinue: OnContinueFunction;
}

export function DemographicInfo({ currentUser, onContinue }: DemographicInfoProps) {
  const { classes } = usePreOnboardingStepStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    await onContinue({
      firstName: values.firstName,
      lastName: values.lastName,
      size: values.organizationSize,
      stepIndex: 0,
      type: values.organizationType,
      website: values.website,
    });
    setIsSubmitting(false);
  };

  return (
    <div className={classes.container}>
      <div className={classes.formWrapper}>
        <Formik
          enableReinitialize
          initialValues={{
            firstName: currentUser?.firstName || '',
            lastName: currentUser?.lastName || '',
            organizationSize: currentUser?.org?.size || Apollo.OrgSize.OneToTen,
            organizationType: currentUser?.org?.type || Apollo.OrgType.Company,
            website: currentUser?.org?.website || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ handleChange, handleReset, handleSubmit, isValid, values }) => {
            return (
              <form className={classes.form} onReset={handleReset} onSubmit={handleSubmit}>
                <div>
                  <FormLabel className={classes.formLabel}>First name *</FormLabel>
                  <FormikAlineInput
                    fullWidth
                    required
                    fieldName={firstNameKey}
                    inputClassName={classes.input}
                    placeholder="Type first name..."
                  />
                </div>
                <div>
                  <FormLabel className={classes.formLabel}>Last name *</FormLabel>
                  <FormikAlineInput
                    fullWidth
                    required
                    fieldName={lastNameKey}
                    inputClassName={classes.input}
                    placeholder="Type last name..."
                  />
                </div>
                <div className={classes.formControlWrapper}>
                  <FormControl fullWidth className={classes.formControl} variant="outlined">
                    <FormLabel className={classes.formLabel} component="legend">
                      Type of organization
                    </FormLabel>
                    <RadioGroup
                      className={classes.organizationTypes}
                      name={organizationTypeKey}
                      value={values[organizationTypeKey]}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        className={classes.radioOption}
                        control={<Radio size="small" />}
                        label="Company"
                        value={OrgType.Company}
                      />
                      <FormControlLabel
                        className={classes.radioOption}
                        control={<Radio size="small" />}
                        label="Law firm"
                        value={OrgType.LawFirm}
                      />
                      <FormControlLabel
                        className={classes.radioOption}
                        control={<Radio size="small" />}
                        label="Academia"
                        value={OrgType.Academia}
                      />
                      <FormControlLabel
                        className={classes.radioOption}
                        control={<Radio size="small" />}
                        label="Other"
                        value={OrgType.Other}
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormControl fullWidth className={classes.formControl} variant="outlined">
                    <FormLabel className={classes.formLabel}>Size of your organization</FormLabel>
                    <Select
                      displayEmpty
                      MenuProps={{
                        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                      }}
                      classes={{ select: classes.selectInput }}
                      name={organizationSizeKey}
                      value={values[organizationSizeKey]}
                      variant="outlined"
                      onChange={handleChange}
                    >
                      <MenuItem value={OrgSize.OneToTen}>1-10</MenuItem>
                      <MenuItem value={OrgSize.TenToFifty}>10-50</MenuItem>
                      <MenuItem value={OrgSize.FiftyToTwoHundred}>50-200</MenuItem>
                      <MenuItem value={OrgSize.TwoHundredPlus}>200+</MenuItem>
                    </Select>
                  </FormControl>
                  <div>
                    <FormLabel className={classes.formLabel}>
                      Website of your organization
                    </FormLabel>
                    <FormikAlineInput
                      fullWidth
                      fieldName={websiteKey}
                      inputClassName={classes.input}
                      placeholder="Add your website..."
                      startAdornment={<span>🌐</span>}
                    />
                  </div>
                </div>
                <Spacer />
                <div className={classes.buttons}>
                  <AlineButton disabled variant="outlined">
                    Go Back
                  </AlineButton>
                  <AlineButton
                    color="primary"
                    disabled={!isValid}
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    Continue
                  </AlineButton>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
